import * as React from 'react';
import Link from '../../../common/components/core/Link';
import classNames from 'classnames';

const DLA_URL = 'https://docs.branch.io/pages/deep-linked-ads';

const ADWORDS_OVERVIEW_LINK = `${DLA_URL}/google-ads-overview/`;
const ADWORDS_UAC_GUIDE_LINK = `${DLA_URL}/google-uac/`;
const FB_GUIDE_LINK = `${DLA_URL}/facebook-ads-overview/`;
const ADWORDS_CONVERSION_GUIDE_LINK = `${DLA_URL}/google-ads-overview/#latest-method-oauth-method`;
const CONTACT_US_LINK = 'https://support.branch.io/support/tickets/new';
const NEED_TO_BE_ADMIN_LINK = `${DLA_URL}/facebook-ads-faq/#i-see-a-warning-underneath-my-ad-account-saying-that-i-need-to-be-an-admin`;

const facebookI13nParams = {
  partner: 'a_facebook',
};

const googleI13nParams = {
  partner: 'a_google_adwords',
};

export const getErrorCodeMap = (pathname) => {
  return {
    1: {
      text: (
        <span>
          We were unable to link our business account to your ad account. Please{' '}
          <Link href={CONTACT_US_LINK} i13nModel={{ label: 'Contact Support Link' }} testid="contact-support-link">
            contact us
          </Link>{' '}
          and include "Facebook MMP + unable to link business account" in the subject.
        </span>
      ),
      type: 'error',
      instrumentationText: '(#1): We were unable to link our business account to your ad account.',
    },
    2: {
      text: (
        <span>
          We were unable to link our system user to your ad account. Please{' '}
          <Link
            href={CONTACT_US_LINK}
            i13nModel={{
              label: 'Contact Support Link',
              params: facebookI13nParams,
            }}
            testid="contact-support-link"
          >
            contact us
          </Link>{' '}
          and include "Facebook MMP + unable to link ad account" in the subject. Include all the ad account ids in the
          message.
        </span>
      ),
      type: 'error',
      instrumentationText: '(#2): We were unable to link our system user to your ad account.',
    },
    3: {
      text: (
        <span>
          This ad account is not linked to a business account, and you are not an admin on an account so could not be linked.
          Please go into the Facebook Account Manager and grant the Branch Ads Manager permission to report on your ads. Once
          you've done this, refresh the page to restart the authentication flow.{' '}
          <Link
            href={NEED_TO_BE_ADMIN_LINK}
            i13nModel={{
              label: 'Need to be admin Link',
              params: facebookI13nParams,
            }}
            testid="need-to-be-admin-link"
          >
            Learn more
          </Link>
          .
        </span>
      ),
      type: 'warning',
      instrumentationText:
        '(#3): This ad account is not linked to a business account, and you are not an admin on an account so could not be linked.',
    },
    4: {
      text: (
        <span>
          There are errors on the{' '}
          <Link
            to={{
              pathname,
              query: {
                tab: 'attribution_windows',
              },
            }}
            i13nModel={{
              label: 'Go To Attribution Windows Tab',
            }}
            testid="attribution-windows-link"
          >
            attribution windows tab
          </Link>
          . Please correct these before proceeding.
        </span>
      ),
      type: 'error',
      instrumentationText: '(#4): there are errors on the ad attribution windows tab.',
    },
  };
};

class a_google_adwords_text extends React.PureComponent {
  render() {
    return (
      <div className="account-settings-text">
        <span>
          Google AdWords credentials are only required for Universal App Campaigns or conversion tracking. You can enable
          Google AdWords without entering credentials. Please see our{' '}
        </span>
        <Link
          href={ADWORDS_OVERVIEW_LINK}
          i13nModel={{
            label: 'Google AdWords docs link - old flow',
            params: googleI13nParams,
          }}
          testid="account-settings__google-docs-link"
        >
          documentation
        </Link>
        <span> for more details on setting up Google AdWords campaigns.</span>
      </div>
    );
  }
}

export const ACCOUNT_SETTINGS_TEXTS = {
  a_google_adwords: a_google_adwords_text,
  a_facebook: class extends React.PureComponent {
    render() {
      return (
        <div className="account-settings-text">
          <span>
            To track Facebook Ads, please ensure you have followed the Facebook Advertising Setup Instructions. Please see
            our{' '}
          </span>
          <Link
            href={FB_GUIDE_LINK}
            i13nModel={{
              label: 'Facebook docs link',
              params: facebookI13nParams,
            }}
            testid="account-settings__facebook-docs-link"
          >
            documentation
          </Link>
          <span> for more details on setting up Facebook campaigns.</span>
        </div>
      );
    }
  },
};

export const CREDENTIALS_TOOLTIPS = {
  a_google_adwords: class extends React.PureComponent {
    render() {
      return (
        <div>
          <span>For more information on AdWords Conversion Labels and IDs, please read our documentation on </span>
          <Link
            href={ADWORDS_UAC_GUIDE_LINK}
            i13nModel={{
              label: 'Google AdWords UAC docs link',
              params: googleI13nParams,
            }}
            testid="tooltip__google-uac-docs-link"
          >
            Universal App Campaigns
          </Link>
          <span> and </span>
          <Link
            href={ADWORDS_CONVERSION_GUIDE_LINK}
            i13nModel={{
              label: 'Google AdWords conversion docs link',
              params: googleI13nParams,
            }}
            testid="tooltip__google-conversion-docs-link"
          >
            AdWords Conversion Tracking
          </Link>
          <span>.</span>
        </div>
      );
    }
  },
};

export const NoAdAccountsMessage = (props: { openReauthModal: () => void; adNetwork: string; machineName: string }) => (
  <div>
    <span>There are no ad accounts available based on the user you authenticated with {props.adNetwork}. Please </span>
    <Link
      onClick={props.openReauthModal}
      i13nModel={{ label: 'Open Reauthenticate Modal from no options' }}
      testid={`no-options__${props.machineName}-reauth-modal-link`}
    >
      reauthenticate
    </Link>
    <span> as a different user to start again.</span>
  </div>
);

export const WARNING_MODAL_REAUTH_TITLE = 'Are you sure you want to reauthenticate?';
export const WARNING_MODAL_CONFIRM_BUTTON = 'Yes, Reauthenticate';
export const getWarningModalReauthText = (partner) =>
  `If you reauthenticate, you could potentially break your integration ${
    partner ? `with ${partner}` : ''
  } if you complete the process with different
  credentials. Please only take this step after discussing with a
  Branch team member.`;

export const NoAppWarning = () => (
  <div>
    <i className="material-icons status_message_icon h_fz-body1 h_c-warning h_mend-5 h_mb-5">warning</i>
    No iTunes ID or Play ID found in your Configuration. Please configure your app in{' '}
    <Link to={'/configuration'} i13nLabel="no-app-link-settings-link" testid="no-app-link-settings-link">
      Configuration
    </Link>{' '}
    to continue.
  </div>
);

export const AppValidation = ({ mobileAppID, hasApp, className }) =>
  hasApp ? (
    <div className={classNames(className)}>
      <i className="material-icons status_message_icon h_fz-body1 h_c-success h_mend-5 h_mb-5">check_circle</i>
      {mobileAppID} found
    </div>
  ) : (
    <div>
      <i className="material-icons status_message_icon h_fz-body1 h_c-warning h_mend-5 h_mb-5">warning</i>
      No {mobileAppID} found. Please configure your app in{' '}
      <Link to={'/configuration'} i13nLabel={'no-app-saved-link-setting-link'} testid="no-app-saved-link-settings-link">
        Configuration
      </Link>
      .
    </div>
  );

export const AdAccountsErrorMessage = (props: {
  openReauthModal: () => void;
  adNetwork: string;
  machineName: string;
  errorMessages?: string[];
}) => (
  <div>
    <span>An error occured while fetching ad accounts for {props.adNetwork}. Please </span>
    <Link
      onClick={props.openReauthModal}
      i13nModel={{
        label: 'Open Reauthenticate Modal from ad account fetch error',
      }}
      testid={`get-accounts__${props.machineName}-reauth-modal-link`}
    >
      reauthenticate
    </Link>
    <span> as a different user.</span>
    {props.errorMessages && (
      <ul>
        {props.errorMessages.map((errorMessage, index) => (
          <li key={index}>{errorMessage}</li>
        ))}
      </ul>
    )}
  </div>
);

export const AuthenticationErrorMessage = ({ show }) => (
  <span
    className={classNames('ad-partner-header__error-message', {
      show: show,
    })}
  >
    <i className="material-icons status_message_icon h_fz-body1 h_c-error h_mend-5 h_mb-5">error</i>
    error attempting to authenticate.
  </span>
);

export const getPartnerSettingsToggleText = (inheriting: boolean, orgName: string): React.ReactElement<any> =>
  inheriting ? (
    <span>
      <span className="h_fw-700">You are inheriting</span> this ad partner's settings from your app's parent organization,{' '}
      {orgName}. In order to edit settings or postbacks, please change this toggle to not inherit from the organization, or
      use the selector in the top left to make changes for the entire {orgName} organization.
    </span>
  ) : (
    <span>You are not inheriting this ad partner's settings from your app's parent organization, {orgName}.</span>
  );
